/**
 * Delad marknadsattribution — EN capture-logik för hela Elido-sajten.
 * -------------------------------------------------------------------
 * Fångar gclid/gbraid/wbraid/fbclid + utm_* + landing_url + referrer +
 * first-touch/last-touch timestamp från URL:en och persisterar i
 * sessionStorage så att first-touch bevaras även om användaren
 * scrollar/nav:erar innan ett formulär skickas.
 *
 * Alla lead-formulär (kontakt, gratis-demo, ny-hemsida) återanvänder
 * dessa två funktioner — ingen parallell variant per sida.
 *
 * Ingen persistent cookie sätts här; Consent Mode v2 styr all
 * cookie-baserad spårning globalt (utils/cookieConsent.js).
 */

import { getConsent } from "@/utils/cookieConsent";

const STORAGE_KEY = "elido_attribution_v1";
const CLICK_ID_KEYS = ["gclid", "gbraid", "wbraid", "fbclid"];
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];

// Attribution writes are marketing-tier storage under Consent Mode v2 and
// must be gated on marketing=true. cookieConsent has no imports of its own
// so a plain static import is safe (no circular dep).
function marketingConsented() {
  try {
    const c = getConsent();
    return !!(c && c.marketing);
  } catch {
    return false;
  }
}

function safeStorage() {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

function getExisting() {
  const s = safeStorage();
  if (!s) return {};
  try {
    const raw = s.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function persist(data) {
  const s = safeStorage();
  if (!s) return;
  try {
    s.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* full storage / private mode — ignore */
  }
}

/**
 * Läs URL-parametrar, slå ihop med redan sparad first-touch attribution
 * och persistera. Idempotent — anropa vid varje sidmount.
 */
export function captureAttribution() {
  if (typeof window === "undefined") return {};
  // Consent Mode v2: no marketing attribution to sessionStorage before
  // the visitor accepts marketing. When they accept, this is called
  // again (from the consent-updated listener) and the current URL still
  // carries any click-id / utm_* params so first-touch is preserved.
  if (!marketingConsented()) return {};
  let params;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return getExisting();
  }
  const existing = getExisting();
  const next = { ...existing };

  // First-touch: skriv aldrig över ett redan fångat click-id/utm.
  for (const k of CLICK_ID_KEYS) {
    const v = params.get(k);
    if (v && !next[k]) next[k] = v;
  }
  for (const k of UTM_KEYS) {
    const v = params.get(k);
    if (v && !next[k]) next[k] = v;
  }

  if (!next.landing_url) next.landing_url = window.location.href;
  if (!next.referrer) next.referrer = document.referrer || "";
  const nowIso = new Date().toISOString();
  if (!next.first_seen_at) next.first_seen_at = nowIso;
  if (!next.timestamp) next.timestamp = nowIso;
  next.last_seen_at = nowIso;

  persist(next);
  return next;
}

/** Returnera nuvarande attribution utan att skriva (för formulär-payload).
 *  Endast om marketing-consent finns — annars {} (Consent Mode v2). */
export function getAttribution() {
  if (!marketingConsented()) return {};
  return getExisting();
}
